import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { z } from "zod";
import { currentLocationSchema } from "../../../../components/shared/table/CurrentLocationColumn";
import { laneTypeMap } from "../../../../types/outbound/lane.type";
import { http } from "../../../../utils/httpCommon";
import { useApiUrls } from "../../../useApiUrl";
import { LaneDetail } from "./useFreightDetails";
import { Shipment, ShipmentsByLaneSummary } from "./useGetShipmentDetails";

const billsByTagSchema = z.object({
  id: z.number(),
  name: z.string(),
  color: z.string(),
  bills: z.number(),
  position: z.number(),
});

const osadSummarySchema = z.object({
  areaName: z.string(),
  bills: z.number(),
  pieces: z.number(),
  pallets: z.number(),
  weight: z.number(),
  handlingUnits: z.number(),
  billsByTags: z.array(billsByTagSchema),
});

const shipmentTagSchema = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string(),
  color: z.string(),
  position: z.number(),
  bills: z.number(),
});

const osadShipmentSchema = z.object({
  shipmentId: z.number(),
  proNumber: z.string(),
  reasonId: z.number().nullish(),
  reasonCode: z.string().nullish(),
  pallets: z.number(),
  pieces: z.number(),
  weight: z.number(),
  weightUnit: z.string(),
  handlingUnits: z.number(),
  tags: z.array(shipmentTagSchema),
  originSic: z.string(),
  destinationSic: z.string(),
  finalDestinationSic: z.string(),
  status: z.string(),
  dueServiceDate: z.coerce.string(),
  loadToSicId: z.number().nullish(),
  loadTo: z.string(),
  currentLocation: currentLocationSchema,
  isPlannedOB: z.boolean(),
});

const osadDetailsSchema = z.object({
  summary: osadSummarySchema,
  shipments: z.array(osadShipmentSchema),
});

type OsadDetails = z.infer<typeof osadDetailsSchema>;
type OsadSummary = z.infer<typeof osadSummarySchema>;
type OsadShipment = z.infer<typeof osadShipmentSchema>;

export function mapOsadToLaneDetail(
  summary: OsadSummary,
  sicId: number
): LaneDetail {
  return {
    bills: summary.bills,
    billsByTags: summary.billsByTags,
    capacityWeight: 0,
    destinationSic: summary.areaName,
    destinationSicId: sicId,
    doors: [],
    doorsCount: 0,
    handlingUnits: summary.handlingUnits,
    indicators: {
      unassignedShipmentsCount: 0,
      shipmentsThatWillMissServiceCount: 0,
      pullsWithNoTrailerCount: 0,
      pullsWithNoTripsAndTrapsCount: 0,
      hasNoDoors: false,
    },
    laneType: laneTypeMap.Osad,
    loadedBills: 0,
    loadedHandlingUnits: 0,
    loadedPallets: 0,
    loadedPieces: 0,
    loadedWeight: 0,
    pallets: summary.pallets,
    pieces: summary.pieces,
    plannedPulls: 0,
    publishedTrips: 0,
    totalWeight: summary.weight,
    laneIdType: `${sicId}.${laneTypeMap.Osad}`,
  };
}

export function mapOsadShipmentToShipment(
  osadShipment: OsadShipment
): Shipment {
  return {
    shipmentId: osadShipment.shipmentId,
    proNumber: osadShipment.proNumber,
    doorNumber: null,
    originSic: osadShipment.originSic,
    loadToSic: osadShipment.loadTo,
    originalLane: null,
    shipmentDivertedByUser: null,
    localTime: null,
    laneType: null,
    destinationSic: osadShipment.destinationSic,
    trailerNumber: osadShipment.currentLocation.trailer ?? "",
    tripNumber: osadShipment.currentLocation.tripNumber ?? null,
    serviceDue: osadShipment.dueServiceDate
      ? dayjs(osadShipment.dueServiceDate)
      : null,
    pickUpDate: null,
    pieces: osadShipment.pieces,
    handlingUnits: osadShipment.handlingUnits,
    pallets: osadShipment.pallets,
    weight: osadShipment.weight,
    willMakeService: true,
    isUnassigned: false,
    status: osadShipment.status,
    isLoaded: null,
    reasonId: osadShipment.reasonId,
    reasonCode: osadShipment.reasonCode,
    tags: osadShipment.tags.map((t) => ({
      id: t.id,
      name: t.name,
      color: t.color,
    })),
    currentLocation: osadShipment.currentLocation,
  };
}

const TONNAGE_COLORS = [
  "#607D8B",
  "#90A4AE",
  "#455A64",
  "#CFD8DC",
  "#78909C",
  "#546E7A",
  "#B0BEC5",
  "#37474F",
  "#ECEFF1",
  "#263238",
];

export function mapOsadSummaryToShipmentsByLaneSummary(
  summary: OsadSummary,
  shipments: OsadShipment[]
): ShipmentsByLaneSummary {
  const groupedBySic = new Map<
    string,
    {
      sicId: number | null;
      sic: string;
      weight: number;
      bills: number;
      pallets: number;
      pieces: number;
      handlingUnits: number;
    }
  >();

  for (const s of shipments) {
    const key = s.destinationSic;
    const existing = groupedBySic.get(key);
    if (existing) {
      existing.weight += s.weight;
      existing.bills += 1;
      existing.pallets += s.pallets;
      existing.pieces += s.pieces;
      existing.handlingUnits += s.handlingUnits;
    } else {
      groupedBySic.set(key, {
        sicId: s.loadToSicId ?? null,
        sic: key,
        weight: s.weight,
        bills: 1,
        pallets: s.pallets,
        pieces: s.pieces,
        handlingUnits: s.handlingUnits,
      });
    }
  }

  const tonnageByFinalDestination = Array.from(groupedBySic.values()).map(
    (group, index) => ({
      ...group,
      interchangePoint: null,
      color: TONNAGE_COLORS[index % TONNAGE_COLORS.length],
    })
  );

  return {
    bills: summary.bills,
    loadedBills: 0,
    weight: summary.weight,
    loadedWeight: 0,
    pieces: summary.pieces,
    handlingUnits: summary.handlingUnits,
    pallets: summary.pallets,
    tonnageByFinalDestination,
    indicators: {
      unassignedBillsCount: 0,
      missServiceBillsCount: 0,
      noTrailersBillsCount: 0,
      noTripsBillsCount: 0,
      noDoorBillsCount: 0,
    },
  };
}

const keys = {
  osadDetails: (sicId: number, planDate?: string) => [
    "outbound",
    "freightDetails",
    "osadDetails",
    sicId,
    ...(planDate ? [planDate] : []),
  ],
  osadReasonCodes: () => ["outbound", "freightDetails", "osadReasonCodes"],
};

type UseGetOsadDetailsArgs = {
  sicId: number;
  planDate?: string;
  enabled?: boolean;
};

export function useGetOsadDetails({
  sicId,
  planDate,
  enabled = true,
}: UseGetOsadDetailsArgs) {
  const urls = useApiUrls();

  return useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps -- urls is stable during session
    queryKey: keys.osadDetails(sicId, planDate),
    enabled,
    queryFn: async () => {
      const { data } = await http.get<OsadDetails>(
        urls.osad.getOsadDetails(sicId),
        { params: { ...(planDate && { planDate }) } }
      );
      return osadDetailsSchema.parse(data);
    },
  });
}

const osadReasonCodeSchema = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string().nullish(),
});

type OsadReasonCode = z.infer<typeof osadReasonCodeSchema>;

export function useGetOsadReasonCodes(enabled = true) {
  const urls = useApiUrls();

  return useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: keys.osadReasonCodes(),
    enabled,
    queryFn: async () => {
      const { data } = await http.get<OsadReasonCode[]>(
        urls.osad.getReasonCodes
      );
      return z.array(osadReasonCodeSchema).parse(data);
    },
  });
}

type ChangeReasonCodeArgs = {
  onSuccess?: () => void;
  onError?: () => void;
  sicId: number;
  method: "put" | "post";
};

type ChangeReasonCodeRequest = {
  reasonId: number;
  shipmentIds: number[];
};

export function useChangeOsadReasonCode({
  onSuccess,
  onError,
  sicId,
  method,
}: ChangeReasonCodeArgs) {
  const urls = useApiUrls();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: ChangeReasonCodeRequest) => {
      await http[method](urls.osad.createOSAD(sicId), {
        reasonId: request.reasonId,
        shipmentIds: request.shipmentIds,
        moduleId: null, // This field is not being used in the backend, so we can send null for now
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: keys.osadDetails(sicId),
      });
      queryClient.invalidateQueries({
        queryKey: keys.osadReasonCodes(),
      });
      queryClient.invalidateQueries({
        queryKey: ["outbound", "laneDetails", "shipment"],
      });
      onSuccess?.();
    },
    onError: () => {
      onError?.();
    },
  });
}
